// studentapps/ResourceLibrary.js
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Link as LinkIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  CorporateFare as CorporateIcon
} from '@mui/icons-material';

const ResourceLibrary = () => {
  const [resources, setResources] = useState([
    {
      id: 1,
      title: 'Khan Academy - Math',
      url: 'https://www.khanacademy.org/math',
      type: 'website',
      subject: 'Mathematics',
      description: 'Free online math courses and practice exercises for all levels',
      tags: ['free', 'videos', 'exercises']
    },
    {
      id: 2,
      title: 'Physics Classroom',
      url: 'https://www.physicsclassroom.com',
      type: 'website',
      subject: 'Physics',
      description: 'Physics tutorials and interactive problems with detailed solutions',
      tags: ['tutorials', 'interactive']
    },
    {
      id: 3,
      title: 'Organic Chemistry Tutor',
      url: 'https://www.youtube.com/c/TheOrganicChemistryTutor',
      type: 'video',
      subject: 'Chemistry',
      description: 'YouTube channel with comprehensive chemistry tutorials and problem walkthroughs',
      tags: ['youtube', 'tutorials']
    },
    {
      id: 4,
      title: 'Coursera - Computer Science',
      url: 'https://www.coursera.org/browse/computer-science',
      type: 'website',
      subject: 'Computer Science',
      description: 'Online courses from top universities on programming and computer science',
      tags: ['courses', 'programming']
    }
  ]);

  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [newResource, setNewResource] = useState({
    title: '',
    url: '',
    type: 'website',
    subject: '',
    description: '',
    tags: []
  });

  const filteredResources = resources.filter(resource =>
    resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    resource.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleAddResource = () => {
    if (newResource.title && newResource.url) {
      const resource = {
        id: Date.now(),
        ...newResource
      };
      setResources([...resources, resource]);
      setNewResource({ title: '', url: '', type: 'website', subject: '', description: '', tags: [] });
      setOpenDialog(false);
    }
  };

  const handleDeleteResource = (id) => {
    setResources(resources.filter(resource => resource.id !== id));
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'website': return <LinkIcon />;
      case 'document': return <DescriptionIcon />;
      case 'video': return <VideoIcon />;
      default: return <LinkIcon />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'website': return 'primary';
      case 'document': return 'secondary';
      case 'video': return 'error';
      default: return 'default';
    }
  };

  const subjects = [...new Set(resources.map(r => r.subject))];
  const types = [...new Set(resources.map(r => r.type))];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            Resource Library
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Curated learning materials and references
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          size="large"
        >
          Add Resource
        </Button>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search resources by title, subject, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            size="small"
          />
          
          <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
            <Chip 
              label="All Resources" 
              clickable 
              variant={searchTerm === '' ? 'filled' : 'outlined'}
              onClick={() => setSearchTerm('')}
            />
            {subjects.map(subject => (
              <Chip 
                key={subject} 
                label={subject} 
                clickable 
                variant={searchTerm === subject ? 'filled' : 'outlined'}
                onClick={() => setSearchTerm(subject)}
                color="primary"
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Resources List */}
        <Grid item xs={12} lg={8}>
          <Grid container spacing={2}>
            {filteredResources.map((resource) => (
              <Grid item xs={12} key={resource.id}>
                <Card>
                  <ListItem>
                    <ListItemIcon>
                      <IconButton sx={{ 
                        color: `${getTypeColor(resource.type)}.main`,
                        background: `${getTypeColor(resource.type)}.light`
                      }}>
                        {getTypeIcon(resource.type)}
                      </IconButton>
                    </ListItemIcon>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {resource.title}
                          </Typography>
                          <Chip 
                            label={resource.type} 
                            size="small" 
                            color={getTypeColor(resource.type)}
                          />
                          <Chip 
                            label={resource.subject} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {resource.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {resource.tags.map((tag, index) => (
                              <Chip key={index} label={tag} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      }
                    />
                    
                    <ListItemIcon>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Button
                          href={resource.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<LinkIcon />}
                          variant="outlined"
                          size="small"
                        >
                          Visit
                        </Button>
                        <IconButton
                          onClick={() => handleDeleteResource(resource.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </ListItemIcon>
                  </ListItem>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filteredResources.length === 0 && (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No resources found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm ? 'Try adjusting your search terms' : 'Add your first resource to get started'}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Statistics and Quick Actions */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Library Stats
                </Typography>
                <Chip 
                  label="Zolang Nexora" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  icon={<CorporateIcon />}
                />
              </Box>
              
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" color="primary" fontWeight="700">
                  {resources.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Resources
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {types.map(type => {
                  const count = resources.filter(r => r.type === type).length;
                  return (
                    <Box key={type} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                        {type}s
                      </Typography>
                      <Chip label={count} size="small" color={getTypeColor(type)} />
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="outlined" fullWidth startIcon={<DownloadIcon />}>
                  Export Library
                </Button>
                <Button variant="outlined" fullWidth startIcon={<AddIcon />}>
                  Import Resources
                </Button>
                <Button variant="outlined" fullWidth>
                  Suggest Resources
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Resource Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="600">
            Add New Resource
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Resource Title"
            fullWidth
            variant="outlined"
            value={newResource.title}
            onChange={(e) => setNewResource({...newResource, title: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="URL"
            fullWidth
            variant="outlined"
            value={newResource.url}
            onChange={(e) => setNewResource({...newResource, url: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="Type"
            fullWidth
            variant="outlined"
            value={newResource.type}
            onChange={(e) => setNewResource({...newResource, type: e.target.value})}
            sx={{ mb: 2 }}
          >
            <MenuItem value="website">Website</MenuItem>
            <MenuItem value="document">Document</MenuItem>
            <MenuItem value="video">Video</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Subject"
            fullWidth
            variant="outlined"
            value={newResource.subject}
            onChange={(e) => setNewResource({...newResource, subject: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newResource.description}
            onChange={(e) => setNewResource({...newResource, description: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Tags (comma-separated)"
            fullWidth
            variant="outlined"
            placeholder="free, interactive, tutorials"
            onChange={(e) => setNewResource({...newResource, tags: e.target.value.split(',').map(tag => tag.trim())})}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleAddResource} variant="contained">
            Add Resource
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ResourceLibrary;